import React, { useEffect, useMemo, useState } from 'react';
import { api, ApiError } from './api';
import { EstadoError } from './EstadoError';
import { listarVentasOffline } from './offlineDb';
import { pendientesDelTurno } from './offlineRevision';

const DENOMINACIONES_INICIALES = [
  ...[1, 2, 5, 10, 20, 50].map((valor) => ({ tipo: 'moneda', valor, cantidad: '' })),
  ...[10, 20, 50, 100, 200, 500, 1000, 2000, 10000, 20000].map((valor) => ({ tipo: 'billete', valor, cantidad: '' })),
];

const pesos = (valor) => Number(valor || 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' });

export default function Caja({ onEstadoCajaChange, usuario }) {
  const [cajaActual, setCajaActual] = useState(null);
  const [errorCarga, setErrorCarga] = useState('');
  const [montoInicial, setMontoInicial] = useState('');
  const [montoMovimiento, setMontoMovimiento] = useState('');
  const [descMovimiento, setDescMovimiento] = useState('');
  const [tipoMovimiento, setTipoMovimiento] = useState('Egreso');
  const [observacionesCierre, setObservacionesCierre] = useState('');
  const [denominaciones, setDenominaciones] = useState(DENOMINACIONES_INICIALES);
  const [cerrando, setCerrando] = useState(false);

  const cargarEstadoCaja = async () => {
    setErrorCarga('');
    try {
      const data = await api.get(`/api/caja/actual?t=${Date.now()}`);
      if (typeof onEstadoCajaChange === 'function') onEstadoCajaChange();
      if (data.abierta && data.turno) {
        setCajaActual({
          turno: data.turno,
          movimientos: data.movimientos || [],
          ventasEfectivo: Number(data.ventasEfectivo || 0),
          ventasDigitales: Number(data.ventasDigitales || 0),
          ventasCuentaCorriente: Number(data.ventasCuentaCorriente || 0),
          efectivoEsperado: Number(data.efectivoEsperado || 0),
          esTurnoPropio: Boolean(data.es_turno_propio),
          puedeOperar: Boolean(data.puede_operar),
        });
      } else setCajaActual(null);
    } catch (err) {
      setErrorCarga(err instanceof ApiError ? err.mensaje : 'No se pudo cargar el estado de la caja.');
    }
  };

  useEffect(() => { cargarEstadoCaja(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const abrirCaja = async () => {
    if (montoInicial === '') return alert('Seleccioná o ingresá con cuánto cambio arrancás la caja.');
    try {
      const data = await api.post('/api/caja/abrir', { monto_inicial: Number(montoInicial) });
      if (!data.success || !data.turno) return alert('El servidor rechazó la apertura.');
      setMontoInicial('');
      await cargarEstadoCaja();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) await cargarEstadoCaja();
      alert('❌ ' + (err instanceof ApiError ? err.mensaje : 'No se pudo abrir la caja.'));
    }
  };

  const registrarMovimiento = async () => {
    if (!montoMovimiento || !descMovimiento.trim()) return alert('Completá monto y descripción.');
    try {
      await api.post('/api/caja/movimiento', {
        turno_id: cajaActual.turno.id,
        tipo: tipoMovimiento,
        monto: Number(montoMovimiento),
        descripcion: descMovimiento.trim(),
      });
      setMontoMovimiento('');
      setDescMovimiento('');
      await cargarEstadoCaja();
    } catch (err) {
      alert('❌ ' + (err instanceof ApiError ? err.mensaje : 'No se pudo guardar el movimiento.'));
    }
  };

  const totalArqueo = useMemo(() => denominaciones.reduce((total, item) => {
    const cantidad = Number.parseInt(item.cantidad || '0', 10) || 0;
    return total + Number(item.valor || 0) * cantidad;
  }, 0), [denominaciones]);

  const cambiarCantidad = (indice, valor) => {
    const limpio = valor === '' ? '' : String(Math.max(0, Number.parseInt(valor, 10) || 0));
    setDenominaciones((prev) => prev.map((item, i) => i === indice ? { ...item, cantidad: limpio } : item));
  };

  const agregarDenominacion = () => {
    const valor = window.prompt('Valor de la nueva denominación:');
    if (valor == null) return;
    const numero = Number(valor.replace(',', '.'));
    if (!Number.isFinite(numero) || numero <= 0) return alert('Valor inválido.');
    const tipo = window.confirm('Aceptar = billete / Cancelar = moneda') ? 'billete' : 'moneda';
    setDenominaciones((prev) => [...prev, { tipo, valor: numero, cantidad: '' }]);
  };

  const cerrarCaja = async () => {
    if (cerrando) return;
    setCerrando(true);
    try {
      let anteriores = 0;
      if (typeof indexedDB !== 'undefined') {
        const locales = await listarVentasOffline({ usuario, incluirSincronizadas: false });
        const pendientes = pendientesDelTurno(locales, cajaActual.turno.id);
        anteriores = locales.filter(v => ['pendiente','sincronizando','conflicto'].includes(v.estado)).length - pendientes.length;
        if (pendientes.length) {
          alert(`Hay ${pendientes.length} venta(s) sin registrar en este dispositivo. Abrí el indicador de conexión y sincronizalas o revisalas antes de cerrar la caja.`);
          return;
        }
      }
      const aviso = anteriores ? `\n\nHay ${anteriores} venta(s) de otros turnos pendientes de revisión. Seguirán conservadas en este dispositivo y no están incluidas en esta caja.` : '';
      if (!window.confirm(`¿Cerrar el turno con un efectivo contado de ${pesos(totalArqueo)}?${aviso}`)) return;
      const data = await api.post('/api/caja/cerrar', {
        turno_id: cajaActual.turno.id,
        efectivo_contado: Number(totalArqueo.toFixed(2)),
        denominaciones: denominaciones.map((d) => ({
          tipo: d.tipo,
          valor: Number(d.valor),
          cantidad: Number.parseInt(d.cantidad || '0', 10) || 0,
        })),
        observaciones: observacionesCierre.trim() || null,
      });
      const diferencia = Number(data.diferencia || 0);
      let mensaje = `✅ Caja cerrada.\n\nEsperado: ${pesos(data.efectivo_esperado)}\nContado: ${pesos(data.efectivo_contado)}\nDiferencia: ${pesos(diferencia)}`;
      if (Math.abs(diferencia) > 0.01) mensaje += diferencia < 0 ? '\n\n⚠️ Faltante de caja.' : '\n\n⚠️ Sobrante de caja.';
      alert(mensaje);
      setDenominaciones(DENOMINACIONES_INICIALES);
      setObservacionesCierre('');
      setCajaActual(null);
      if (typeof onEstadoCajaChange === 'function') onEstadoCajaChange();
    } catch (err) {
      alert('❌ ' + (err instanceof ApiError ? err.mensaje : 'No se pudo cerrar la caja.'));
      if (err instanceof ApiError && err.status === 409) await cargarEstadoCaja();
    } finally { setCerrando(false); }
  };

  if (errorCarga) return <EstadoError mensaje={errorCarga} onReintentar={cargarEstadoCaja} />;

  const esAdmin = String(usuario?.rol || '').toLowerCase() === 'admin';
  if (cajaActual?.turno && !cajaActual.esTurnoPropio && !esAdmin) {
    return <div style={centrado}><div style={{ ...tarjeta, maxWidth: 520, textAlign: 'center', border: '2px solid #fdba74', background: '#fff7ed' }}>
      <h2 style={{ color: '#c2410c' }}>Caja abierta por otro usuario</h2>
      <p>El turno activo pertenece a <strong>{cajaActual.turno.cajero}</strong>.</p>
      <button onClick={cargarEstadoCaja} style={botonPrimario}>Actualizar estado</button>
    </div></div>;
  }

  if (!cajaActual?.turno) {
    return <div style={centrado}><div style={{ ...tarjeta, width: 400, textAlign: 'center' }}>
      <h2>🔒 Caja cerrada</h2>
      <p style={{ color: '#64748b' }}>Abrí el turno a nombre de <strong>{usuario?.username}</strong>.</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 8, marginBottom: 14 }}>
        {[0, 2000, 5000, 10000].map((valor) => <button key={valor} onClick={() => setMontoInicial(String(valor))}
          style={{ padding: 11, borderRadius: 8, border: String(montoInicial) === String(valor) ? '2px solid #10b981' : '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontWeight: 'bold' }}>
          {pesos(valor)}
        </button>)}
      </div>
      <input type="number" min="0" step="0.01" value={montoInicial} onChange={(e) => setMontoInicial(e.target.value)} placeholder="Otro monto"
        style={{ width: '100%', boxSizing: 'border-box', padding: 12, marginBottom: 14, border: '1px solid #cbd5e1', borderRadius: 6 }} />
      <button onClick={abrirCaja} style={{ ...botonPrimario, width: '100%', background: '#10b981' }}>🔓 Abrir caja</button>
    </div></div>;
  }

  const ingresos = (cajaActual.movimientos || []).filter((m) => m.tipo === 'Ingreso').reduce((s, m) => s + Number(m.monto || 0), 0);
  const egresos = (cajaActual.movimientos || []).filter((m) => m.tipo === 'Egreso').reduce((s, m) => s + Number(m.monto || 0), 0);
  const esperado = cajaActual.efectivoEsperado || (Number(cajaActual.turno.monto_inicial || 0) + cajaActual.ventasEfectivo + ingresos - egresos);
  const diferenciaPrevia = Number((totalArqueo - esperado).toFixed(2));

  return <div>
    <h2 style={{ color: '#0f172a', borderBottom: '2px solid #38bdf8', paddingBottom: 10 }}>💰 Control de caja</h2>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 10, marginBottom: 18 }}>
      <Resumen titulo="Fondo inicial" valor={cajaActual.turno.monto_inicial} />
      <Resumen titulo="Ventas efectivo" valor={cajaActual.ventasEfectivo} />
      <Resumen titulo="Ventas digitales" valor={cajaActual.ventasDigitales} />
      <Resumen titulo="Efectivo esperado" valor={esperado} destacado />
    </div>

    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.5fr) minmax(330px,1fr)', gap: 20 }}>
      <div>
        <div style={tarjeta}>
          <h3 style={{ marginTop: 0 }}>📝 Movimientos</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 140px', gap: 8, marginBottom: 12 }}>
            <select value={tipoMovimiento} onChange={(e) => setTipoMovimiento(e.target.value)} style={input}>
              <option>Ingreso</option><option>Egreso</option>
            </select>
            <input value={descMovimiento} onChange={(e) => setDescMovimiento(e.target.value)} placeholder="Motivo" style={input} />
            <input type="number" value={montoMovimiento} onChange={(e) => setMontoMovimiento(e.target.value)} placeholder="Monto" style={input} />
          </div>
          <button onClick={registrarMovimiento} style={botonPrimario}>Guardar movimiento</button>
          <div style={{ overflowX: 'auto', marginTop: 15 }}><table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><Th>Hora</Th><Th>Tipo</Th><Th>Detalle</Th><Th>Monto</Th></tr></thead>
            <tbody>{(cajaActual.movimientos || []).map((m) => <tr key={m.id}>
              <Td>{new Date(m.fecha).toLocaleTimeString('es-AR')}</Td><Td>{m.tipo}</Td><Td>{m.descripcion}</Td><Td>{pesos(m.monto)}</Td>
            </tr>)}</tbody>
          </table></div>
        </div>
      </div>

      <div style={{ ...tarjeta, background: '#0f172a', color: 'white' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div><h3 style={{ margin: 0 }}>🏁 Arqueo por denominación</h3><small style={{ color: '#94a3b8' }}>Ingresá cuántas unidades contaste.</small></div>
          <button onClick={agregarDenominacion} style={{ ...botonPrimario, padding: '7px 10px' }}>＋ Otra</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(115px,1fr))', gap: 8, marginTop: 14 }}>
          {denominaciones.map((d, i) => <label key={`${d.tipo}-${d.valor}-${i}`} style={{ background: '#1e293b', borderRadius: 8, padding: 8, fontSize: 12 }}>
            <span style={{ color: '#cbd5e1' }}>{d.tipo === 'moneda' ? 'Moneda' : 'Billete'} {pesos(d.valor)}</span>
            <input type="number" min="0" step="1" value={d.cantidad} onChange={(e) => cambiarCantidad(i, e.target.value)}
              style={{ ...input, marginTop: 5, textAlign: 'center', fontWeight: 'bold' }} placeholder="0" />
            <small style={{ color: '#94a3b8' }}>{pesos((Number(d.cantidad) || 0) * d.valor)}</small>
          </label>)}
        </div>
        <div style={{ marginTop: 16, padding: 14, borderRadius: 8, background: '#020617' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 22, fontWeight: 'bold' }}><span>Total contado</span><span>{pesos(totalArqueo)}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5, color: Math.abs(diferenciaPrevia) < 0.01 ? '#86efac' : diferenciaPrevia < 0 ? '#fca5a5' : '#fde68a' }}>
            <span>Diferencia estimada</span><strong>{pesos(diferenciaPrevia)}</strong>
          </div>
        </div>
        <textarea value={observacionesCierre} onChange={(e) => setObservacionesCierre(e.target.value)} placeholder="Observaciones del cierre" rows={2}
          style={{ ...input, width: '100%', boxSizing: 'border-box', marginTop: 12, resize: 'vertical' }} />
        <button onClick={cerrarCaja} disabled={cerrando} style={{ ...botonPrimario, width: '100%', marginTop: 12, background: cerrando ? '#94a3b8' : '#ef4444' }}>
          {cerrando ? 'Cerrando…' : '🔒 Cerrar caja'}
        </button>
      </div>
    </div>
  </div>;
}

function Resumen({ titulo, valor, destacado }) {
  return <div style={{ ...tarjeta, padding: 14, textAlign: 'center', background: destacado ? '#e0f2fe' : 'white' }}>
    <small style={{ color: '#64748b', fontWeight: 'bold', textTransform: 'uppercase' }}>{titulo}</small>
    <div style={{ fontSize: 21, fontWeight: 'bold', color: destacado ? '#0369a1' : '#0f172a' }}>{pesos(valor)}</div>
  </div>;
}
const Th = ({ children }) => <th style={{ textAlign: 'left', padding: 9, borderBottom: '2px solid #e2e8f0', color: '#64748b' }}>{children}</th>;
const Td = ({ children }) => <td style={{ padding: 9, borderBottom: '1px solid #e2e8f0' }}>{children}</td>;
const centrado = { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh' };
const tarjeta = { background: 'white', padding: 20, borderRadius: 10, border: '1px solid #e2e8f0' };
const input = { padding: 9, border: '1px solid #cbd5e1', borderRadius: 6, minWidth: 0, boxSizing: 'border-box' };
const botonPrimario = { padding: '10px 16px', border: 'none', borderRadius: 7, background: '#2563eb', color: 'white', fontWeight: 'bold', cursor: 'pointer' };
